import { Overlay, OverlayContainer, ViewportRuler } from '@angular/cdk/overlay';
import { Platform } from '@angular/cdk/platform';
import { Component, Injector, OnDestroy, OnInit } from '@angular/core';
import { CountriesCurrenciesService } from '@app/finxone-web-frontend/app/lib/services/countries-currencies-service/countries-currencies.service';
import { NavigationService } from '@app/finxone-web-frontend/app/lib/services/navigation-service/navigation.service';
import { RoleState, RoleStateModel } from '@app/finxone-web-frontend/app/lib/state/role.state';
import {
  buildHandleBarModel,
  filterIfHandlebarNotRendered,
} from '@app/finxone-web-frontend/app/lib/utils/template/model-builder.utils';
import { createRedirectionUrlFromNavigationOptions } from '@app/finxone-web-frontend/app/lib/utils/utils';
import { formatNameAsUrl } from '@app/finxone-web-frontend/app/lib/utils/zone-url.utils';
import { AutoDestroy } from '@finxone-platform/shared/base-types';
import { IconCacheService } from '@finxone-platform/shared/services';
import {
  APP_ZONES,
  BaseCssProperties,
  DummyRole,
  NavigationOptionEnum,
  NavigationOptionsType,
  ProviderClass,
  QuickLinksWidgetConfig,
  WidgetLayout,
  baseCssConfigStyle,
  commonContentStyleList,
  commonTextContentStyleList,
} from '@finxone-platform/shared/sys-config-types';
import { Select, Store } from '@ngxs/store';
import Handlebars from 'handlebars';
import { Observable, Subject, takeUntil } from 'rxjs';
import { ConfigService } from '../../../../lib/services/config-service/config-service.service';
import { createLoggedOutModalPageUrl } from '../../../zones/modal-utils';
import { PageConfigurationProvider } from '../../../zones/page-configuration.provider';
import { ZonePageUrlProvider } from '../../../zones/zone-url-provider';
import { OVERLAY_ACTIVE_TYPE, OverlayWidgetComponent } from '../overlay/overlay';

@Component({
  standalone: false,
  selector: 'finxone-web-frontend-quick-links-widget',
  templateUrl: './quick-links-widget.component.html',
  providers: [{ provide: OVERLAY_ACTIVE_TYPE, useValue: false }],
})
export class QuickLinksWidgetComponent
  extends OverlayWidgetComponent<QuickLinksWidgetComponent>
  implements OnInit, OnDestroy
{
  @AutoDestroy destroy$: Subject<void> = new Subject<void>();
  public currentWidgetPosition: number;
  public widgetLayout: WidgetLayout;
  public parentComponent = 'menu';
  public quickLinksData: {
    title: string;
    url: string | undefined;
    pageId: string | undefined;
    icon: string | undefined;
    role: string | undefined;
    zone: string | undefined;
    browserBehaviour: string | undefined;
    externalLink: string | undefined;
  } = {
    title: '',
    icon: undefined,
    url: undefined,
    pageId: undefined,
    role: '',
    zone: '',
    browserBehaviour: undefined,
    externalLink: undefined,
  };
  textContentStyle: baseCssConfigStyle;
  public iconStyle?: baseCssConfigStyle;
  @Select(RoleState.getRole)
  roles!: Observable<RoleStateModel>;
  public globalStylingClass = '';
  public isAllowedGlobalStyling = false;
  public activationDelaySeconds?: number;

  private loggedOutZones = [
    APP_ZONES.ONBOARDING,
    APP_ZONES.VERIFICATION,
    APP_ZONES.ORGANISATION_ONBOARDING,
    APP_ZONES.ORGANISATION_VERIFICATION,
  ];

  constructor(
    configService: ConfigService,
    dataIn: ProviderClass<QuickLinksWidgetConfig>,
    private store: Store,
    overlay: Overlay,
    injector: Injector,
    _viewportRuler: ViewportRuler,
    _platform: Platform,
    _overlayContainer: OverlayContainer,
    private countriesCurrenciesService: CountriesCurrenciesService,
    private zonePageUrlProvider: ZonePageUrlProvider,
    public pageConfig: PageConfigurationProvider,
    private iconCacheService: IconCacheService,
    private navigationService: NavigationService,
  ) {
    super(
      dataIn,
      overlay,
      injector,
      QuickLinksWidgetComponent,
      _viewportRuler,
      _platform,
      _overlayContainer,
      configService,
    );
  }

  ngOnInit() {
    if (this.dataIn != undefined) {
      this.attri = this.dataIn.attri ?? null;

      if (this.attri != null) {
        this.quickLinksData.title = '';
        this.setPageUrlData(this.dataIn);
        this.quickLinksData.icon = this.dataIn.attri?.widgetProperties?.icon ?? '';
        this.quickLinksData.externalLink = this.dataIn.attri?.externalUrl ?? null;
        this.quickLinksData.browserBehaviour = this.dataIn.attri?.browserBehaviour ?? null;
        const template = Handlebars.compile(this.dataIn.attri?.widgetProperties?.textContent);
        buildHandleBarModel(
          this.store,
          this.dataIn.attri?.widgetProperties?.textContent,
          this.countriesCurrenciesService,
          this.iconCacheService,
        )
          .pipe(takeUntil(this.destroy$))
          .subscribe((model) => {
            this.quickLinksData.title = filterIfHandlebarNotRendered(template(model));
          });
      }
    }

    this.contentStyle = this.configService.getSpecificBaseCssStyle(this.attri, commonContentStyleList);

    this.textContentStyle = this.configService.getSpecificBaseCssStyle(
      this.attri,
      commonTextContentStyleList,
    );

    this.iconStyle = this.configService.getSpecificBaseCssStyle(this.attri, [
      BaseCssProperties.ICON_COLOR,
      BaseCssProperties.ICON_SIZE,
    ]);
    this.getGlobalStyling();
  }

  private getGlobalStyling() {
    if (
      this.dataIn.attri.widgetProperties?.['globalStylingClass'] &&
      this.dataIn.attri.widgetProperties?.['globalStyling']
    ) {
      this.globalStylingClass = this.dataIn.attri.widgetProperties?.['globalStylingClass'] ?? '';
      this.isAllowedGlobalStyling = this.dataIn.attri.widgetProperties?.['globalStyling'] ?? false;
    }

    if (this.dataIn.attri.widgetProperties?.delayButtonActivation) {
      // Delay activation needs the finx-button disabled/animation support, which only the
      // global-styling render path provides, so force it on regardless of the styling config.
      this.isAllowedGlobalStyling = true;
      this.activationDelaySeconds = this.dataIn.attri.widgetProperties?.activationDelaySeconds;
    }
  }

  public setPageUrlData(dataIn: ProviderClass<QuickLinksWidgetConfig>) {
    let pageUrl: string | undefined = dataIn.attri['pageUrl'] ?? undefined;
    const pageId: string | undefined = dataIn.attri['pageId'] ?? undefined;

    if (pageId) {
      const { pageUrl: url, pageNavigationMetadata } = this.navigationService.buildPageNavigationUrl({
        pageId: pageId || '', // using this it will generate url based on pageId and ignore pageUrl, if pageId is present in widget config
        zone: dataIn.attri?.zone || '',
        pageUrl: pageUrl || '',
        role: dataIn.attri?.role || '',
        navigationOption: dataIn.attri['navigationOption'],
        tabRef: dataIn.attri?.['tabRef'] ?? '',
        pageTransition: dataIn.attri?.['pageTransition'] ?? 'forward',
        browserBehaviour: dataIn.attri?.['browserBehaviour'],
        externalUrl: dataIn.attri?.['externalUrl'],
      });

      if (pageNavigationMetadata) {
        pageUrl = this.checkRouteForParamsAndUpdatePageUrl(url) || '';

        this.quickLinksData.url = pageUrl;
        this.quickLinksData.role = pageNavigationMetadata.persona || '';
        this.quickLinksData.zone = pageNavigationMetadata.zone || '';
        this.quickLinksData.pageId = pageId;
        return; // Exit after handling pageId-based navigation
      }
    }

    // ---------- Fallback to old URL structure if pageId-based navigation metadata is not found ----------

    this.navigateToOldUrlStructure(pageUrl, pageId, dataIn);
  }

  private navigateToOldUrlStructure(
    pageUrl: string | undefined,
    pageId: string | undefined,
    dataIn: ProviderClass<QuickLinksWidgetConfig>,
  ) {
    this.checkForDummyRole();
    pageUrl = this.checkRouteForParamsAndUpdatePageUrl(pageUrl || '');
    if (this.loggedOutZones.includes(dataIn.attri?.zone as APP_ZONES)) {
      this.quickLinksData.url = `/zones/${formatNameAsUrl(dataIn.attri?.zone)}/${pageUrl}/${
        dataIn.attri?.role
      }/default`;

      if (dataIn.attri['navigationOption'] === NavigationOptionEnum.USE_AS_MODAL) {
        const zoneUrl = formatNameAsUrl(dataIn.attri?.zone);
        this.quickLinksData.url = createLoggedOutModalPageUrl({
          zoneUrl,
          pageUrl: pageUrl ?? '',
          role: dataIn.attri?.role,
          url: this.quickLinksData.url,
        });
      }
    } else if (pageUrl) {
      const navigationOptions: NavigationOptionsType = {
        navigationOption: dataIn.attri['navigationOption'],
        zone: dataIn.attri?.zone,
        pageUrl: pageUrl,
        pageId: pageId || '',
        role: dataIn.attri?.role,
        tabRef: dataIn.attri?.['tabRef'] ?? '',
        pageTransition: dataIn.attri?.['pageTransition'] ?? 'forward',
      };
      this.quickLinksData.url = createRedirectionUrlFromNavigationOptions(navigationOptions);
    } else {
      this.quickLinksData.url = `/zones/${formatNameAsUrl(dataIn.attri?.zone)}`;
    }
    this.quickLinksData.role = dataIn.attri?.role;
    this.quickLinksData.zone = dataIn.attri?.zone;
    this.quickLinksData.pageId = pageId;
  }

  /**
   * Checks if current url has id in route params and return updated page url
   * @param pageUrl - page url from widget config
   */
  checkRouteForParamsAndUpdatePageUrl(pageUrl: string): string | undefined {
    let url = pageUrl;
    if (this.zonePageUrlProvider?.['id']) {
      url = `${pageUrl}/${this.zonePageUrlProvider['id']}`;
    }
    return url;
  }

  checkForDummyRole() {
    if (this.dataIn?.attri?.role === DummyRole.EMPTY_ROLE) {
      this.roles
        .subscribe((roleInfo) => {
          this.dataIn.attri.role = roleInfo.role;
        })
        .unsubscribe();
    }
  }
}
